import { useState } from "react";
import { MessageSquare, Mail, MapPin, Facebook, Instagram, Gift, Check, Send, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import FooterBestSellers from "@/components/FooterBestSellers";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = ({ scrollToSection }) => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState("idle");
  const [tipsEmail, setTipsEmail] = useState("");
  const [tipsStatus, setTipsStatus] = useState("idle");

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;

    setNewsletterStatus("loading");
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim().toLowerCase(),
      });
      if (response.data.success) {
        setNewsletterStatus("success");
        setEmail("");
      }
    } catch {
      setNewsletterStatus("error");
    }
  };

  const handleTipsSubmit = async (e) => {
    e.preventDefault();
    if (!tipsEmail || !tipsEmail.includes("@")) return;
    setTipsStatus("loading");
    try {
      const res = await axios.post(`${API}/daily-tips/subscribe`, {
        email: tipsEmail.trim().toLowerCase(),
        frequency: "every_2_days",
        language: language === "fr" ? "fr" : "en",
      });
      if (res.data?.success) {
        setTipsStatus("success");
        setTipsEmail("");
      } else {
        setTipsStatus("error");
      }
    } catch {
      setTipsStatus("error");
    }
  };

  const quickLinks = [
    { label: t("nav.services"), id: "services" },
    { label: t("nav.about"), id: "about" },
    { label: t("nav.locations"), id: "locations" },
    { label: t("testimonials.title"), id: "testimonials" },
    { label: t("payment.title"), id: "payment" },
    { label: t("nav.contact"), id: "contact" },
    { label: language === "fr" ? "Skincare 101" : "Skincare 101", href: "/skincare-101" },
    { label: language === "fr" ? "Carrières" : "Careers", href: language === "fr" ? "/fr/carrieres" : "/careers" },
    { label: language === "fr" ? "Joignez-vous à notre équipe" : "Join Our Team", href: language === "fr" ? "/fr/joignez-notre-equipe" : "/join-our-team" },
  ];

  const services = [
    { en: "Botox", fr: "Botox" },
    { en: "Dermal Fillers", fr: "Agents de Comblement" },
    { en: "Lip Enhancement", fr: "Augmentation des Lèvres" },
    { en: "Micro-Needling", fr: "Micro-Aiguilles" },
    { en: "PRP Therapy", fr: "Thérapie PRP" },
    { en: "Pain Management", fr: "Gestion de la Douleur" },
  ];

  return (
    <footer className="bg-foreground text-background" data-testid="footer">
      {/* Karine's Boutique — Best sellers (Iter 391). Silently hides
          when nothing is published, so this only surfaces once Karine
          publishes products at /admin/store. */}
      <FooterBestSellers language={language} />

      {/* Newsletter Banner */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-500 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-black">
              <Gift className="w-5 h-5" />
              <span className="font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                {language === "fr" ? "Offres exclusives et promotions" : "Exclusive offers & promotions"}
              </span>
            </div>
            
            {newsletterStatus === "success" ? (
              <div className="flex items-center gap-2 text-black bg-white/20 px-4 py-2 rounded-full">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {language === "fr" ? "Bienvenue dans le club VIP!" : "Welcome to the VIP club!"}
                </span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={language === "fr" ? "Votre courriel" : "Your email"}
                  className="px-4 py-2 rounded-full bg-white/90 text-black placeholder-gray-500 text-sm w-48 sm:w-64 focus:outline-none focus:ring-2 focus:ring-black/20"
                  data-testid="footer-newsletter-email"
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === "loading"}
                  className="px-4 py-2 rounded-full bg-black text-white hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm font-medium"
                  data-testid="footer-newsletter-submit"
                >
                  {newsletterStatus === "loading" ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">{language === "fr" ? "S'inscrire" : "Subscribe"}</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Karine's Insider Tips subscribe strip — brand-consistent black + gold */}
      <div className="bg-black py-5 border-t border-amber-600/20" data-testid="footer-insider-tips-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-amber-400">
              <Sparkles className="w-5 h-5" />
              <div>
                <p className="font-medium text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {language === "fr" ? "Karine's Insider Tips" : "Karine's Insider Tips"}
                </p>
                <p className="text-amber-100/70 text-xs">
                  {language === "fr"
                    ? "Un conseil expert tous les 2 jours — Botox, fillers, HydraFacial, laser."
                    : "One expert tip every 2 days — Botox, fillers, HydraFacial, laser."}
                </p>
              </div>
            </div>

            {tipsStatus === "success" ? (
              <div className="flex items-center gap-2 text-amber-300 bg-amber-600/15 px-4 py-2 rounded-full border border-amber-500/40">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {language === "fr" ? "Inscription confirmée!" : "You're subscribed!"}
                </span>
              </div>
            ) : (
              <form onSubmit={handleTipsSubmit} className="flex gap-2" data-testid="footer-tips-form">
                <input
                  type="email"
                  value={tipsEmail}
                  onChange={(e) => setTipsEmail(e.target.value)}
                  placeholder={language === "fr" ? "Votre courriel" : "Your email"}
                  className="px-4 py-2 rounded-full bg-zinc-900 text-white placeholder-zinc-500 text-sm w-48 sm:w-64 border border-amber-600/30 focus:outline-none focus:border-amber-500"
                  data-testid="footer-tips-email"
                />
                <button
                  type="submit"
                  disabled={tipsStatus === "loading"}
                  className="px-4 py-2 rounded-full bg-amber-500 text-black hover:bg-amber-400 transition-colors flex items-center gap-2 text-sm font-medium"
                  data-testid="footer-tips-submit"
                >
                  {tipsStatus === "loading" ? (
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">
                        {language === "fr" ? "Recevoir" : "Get Tips"}
                      </span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
          {tipsStatus === "error" && (
            <p className="text-red-400 text-xs mt-2 text-center sm:text-right">
              {language === "fr" ? "Une erreur s'est produite, réessayez." : "Something went wrong, please try again."}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <span
                className="text-2xl font-semibold"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Karine Lauzon
              </span>
              <span className="ml-2 text-xs font-mono-label bg-primary/20 text-primary px-2 py-1 rounded">
                RN
              </span>
            </div>
            <p className="text-background/70 text-sm mb-6">
              {t("footer.tagline")}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/PreciseRNInjectionist"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-background/10 rounded-full hover:bg-[#1877F2] hover:text-white transition-all duration-300 hover:scale-110"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/karine_precisioninjectionist"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-background/10 rounded-full hover:bg-[#E4405F] hover:text-white transition-all duration-300 hover:scale-110"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.id || link.href}>
                  {link.href ? (
                    <a
                      href={link.href}
                      className="text-background/70 hover:text-primary transition-all duration-200 text-sm footer-link"
                      data-testid={`footer-quicklink-${(link.href || "").replace(/[^a-z0-9]+/gi, "-")}`}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-background/70 hover:text-primary transition-all duration-200 text-sm footer-link"
                    >
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
              <li>
                <a
                  href="/karine-lauzon-rn"
                  className="text-background/70 hover:text-primary transition-all duration-200 text-sm footer-link"
                  data-testid="footer-meet-karine"
                >
                  {language === "fr" ? "Rencontrez Karine" : "Meet Karine"}
                </a>
              </li>
              <li>
                <a
                  href="/team"
                  className="text-background/70 hover:text-primary transition-all duration-200 text-sm footer-link"
                  data-testid="footer-meet-team"
                >
                  {language === "fr" ? "Rencontrez l'Équipe" : "Meet the Team"}
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">{t("nav.services")}</h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.en}>
                  <span className="text-background/70 text-sm">{language === "fr" ? service.fr : service.en}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">{t("footer.contactInfo")}</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="sms:613-850-0805?body=Hi, I'd like to book an appointment"
                  className="flex items-center gap-3 text-background/70 hover:text-primary transition-colors text-sm"
                >
                  <MessageSquare className="w-4 h-4" />
                  {language === "fr" ? "Textez-nous:" : "Text Us:"} 613-850-0805
                </a>
              </li>
              <li>
                <a
                  href="mailto:inquiries@karinelauzon.com"
                  className="flex items-center gap-3 text-background/70 hover:text-primary transition-colors text-sm"
                >
                  <Mail className="w-4 h-4" />
                  inquiries@karinelauzon.com
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-background/70 text-sm">
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="space-y-1.5" data-testid="footer-locations">
                    <div>
                      <div className="font-semibold text-background/90">Ottawa</div>
                      <div>1390 Prince of Wales Drive, Main Floor</div>
                    </div>
                    <div>
                      <div className="font-semibold text-background/90">Kanata</div>
                      <div>320 March Road, 6th Floor · Clinic 603</div>
                    </div>
                    <div>
                      <div className="font-semibold text-background/90">Carleton Place</div>
                      <div>163 Bridge Street, Downtown</div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
            
            {/* Afterpay Badge */}
            <div className="mt-6 p-3 bg-background/10 rounded-lg">
              <p className="text-xs text-background/70 mb-2">{t("footer.weAccept")}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="bg-background/20 px-2 py-1 rounded">Visa</span>
                <span className="bg-background/20 px-2 py-1 rounded">Mastercard</span>
                <span className="bg-background/20 px-2 py-1 rounded">Amex</span>
                <span className="bg-background/20 px-2 py-1 rounded">{language === "fr" ? "Débit" : "Debit"}</span>
                <span className="bg-[#B2FCE4] text-black px-2 py-1 rounded font-medium">Afterpay</span>
                <span className="bg-background/20 px-2 py-1 rounded">Medicard</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-background/50 text-sm">
            © {currentYear} Karine Lauzon RN Clinics™. {t("footer.rights")}
          </p>
          <p className="text-background/40 text-[11px] text-center max-w-md leading-relaxed">
            All content, program names, infographics, and proprietary features (including <strong>The Menopause Restore Program™</strong>, <strong>The Andropause Restore Program™</strong>, <strong>The Lift &amp; Restore Program™</strong>, <strong>Prevention · Age Well™</strong>, and all Crown™ programs) are protected intellectual property. Unauthorized reproduction is prohibited. See our <a href="/terms-of-use" className="underline hover:text-primary">Terms of Use</a>.
          </p>
          <div className="flex gap-4 items-center flex-wrap justify-center">
            <a href="https://www.facebook.com/PreciseRNInjectionist" target="_blank" rel="noopener noreferrer"
              className="text-background/50 hover:text-primary transition-colors text-sm">Facebook</a>
            <a href="https://www.instagram.com/karine_precisioninjectionist" target="_blank" rel="noopener noreferrer"
              className="text-background/50 hover:text-primary transition-colors text-sm">Instagram</a>
            <span className="text-background/20">·</span>
            <a href="/terms-of-use" data-testid="footer-terms-link"
              className="text-background/40 hover:text-primary transition-colors text-xs">
              {language === "fr" ? "Conditions d'utilisation" : "Terms of Use"}
            </a>
            <span className="text-background/20">·</span>
            <a href="/security" data-testid="footer-security-link"
              className="text-background/40 hover:text-primary transition-colors text-xs">
              {language === "fr" ? "Sécurité" : "Security"}
            </a>
            <span className="text-background/20">·</span>
            <a href="/professionals" data-testid="footer-pros-link"
              className="text-background/40 hover:text-primary transition-colors text-xs">
              {language === "fr" ? "Programme Pro" : "Pro Partner Program"}
            </a>
            <span className="text-background/20">·</span>
            <a href="/staff/login" data-testid="footer-team-signin"
              className="text-background/40 hover:text-primary transition-colors text-xs">
              {language === "fr" ? "Connexion équipe" : "Team sign-in"}
            </a>
            <span className="text-background/20">·</span>
            <a href="/signage" target="_blank" rel="noopener noreferrer" data-testid="footer-signage-link"
              className="text-background/40 hover:text-primary transition-colors text-xs">
              {language === "fr" ? "Logo écran de veille" : "Screensaver logo"}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
